import { HOMEBREW_SCHEMA_VERSION } from '../../shared/homebrewSchemas.js';

const DEFAULTS: Record<string, any> = {
  race: { size: 'Medium', speed: 30, languages: ['Common'], traits: [] },
  class: { hit_die: 8, saving_throws: [], features_by_level: [] },
  subclass: { parent_class: '', features_by_level: [] },
  background: { skill_proficiencies: [], tool_proficiencies: [], languages: [] },
  feat: { benefits: [] },
  spell: { level: 1, school: '', casting_time: '', range: '', duration: '', description: '' },
  monster: { size: 'Medium', type: 'humanoid', challenge_rating: '1', armor_class: 10, hit_points: { average: 10, formula: '3d8' }, ability_scores: {}, actions: [] },
  item: { category: 'gear', description: '' },
  weapon: { category: 'simple', weapon_type: 'melee', damage: { dice: '1d6', type: 'slashing' } },
  armor: { category: 'light', armor_type: 'light', ac: { base: 11 } },
  language: { type: 'standard', script: '', typical_speakers: [] },
};

function asStringArray(value: any): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map((v) => v.trim()).filter(Boolean);
  return [];
}

function normalizeSpellComponents(components: any, materialText: any) {
  const fromArray = Array.isArray(components)
    ? components.map((v) => String(v || '').toUpperCase())
    : [];
  const fromObject = components && typeof components === 'object' && !Array.isArray(components)
    ? components
    : {};

  const verbal = fromArray.includes('V') || !!fromObject.verbal || !!fromObject.v;
  const somatic = fromArray.includes('S') || !!fromObject.somatic || !!fromObject.s;
  const material = fromArray.includes('M') || !!fromObject.material || !!fromObject.m;
  const materialsNeeded = String(
    materialText ??
    fromObject.materials_needed ??
    (typeof fromObject.m === 'string' ? fromObject.m : '')
  ).trim();

  return {
    verbal,
    somatic,
    material,
    ...(material && materialsNeeded ? { materials_needed: materialsNeeded } : {}),
  };
}

export function normalizeStructuredHomebrewData(type: string, data: any) {
  const base = { ...(DEFAULTS[type] || {}), ...(data || {}) };

  if (base.tags) base.tags = asStringArray(base.tags);
  if (base.languages) {
    if (Array.isArray(base.languages) || typeof base.languages === 'string') {
      base.languages = asStringArray(base.languages);
    }
  }

  // Backward compatible aliases for creator/sheet consumers
  if (type === 'race') {
    if (base.abilityScoreIncrease && !base.ability_score_increase) base.ability_score_increase = base.abilityScoreIncrease;
    if (base.ability_score_improvements && !base.ability_score_increase) base.ability_score_increase = base.ability_score_improvements;
  }
  if (type === 'class' || type === 'subclass') {
    if (!Array.isArray(base.features_by_level)) base.features_by_level = [];
    if (type === 'class') {
      // Legacy coercion: convert flat feature array -> grouped features_by_level
      if (Array.isArray(base.features_by_level) && base.features_by_level.length > 0) {
        const looksFlat = base.features_by_level.some((f: any) => f && (f.name || f.description) && !Array.isArray(f.features));
        if (looksFlat) {
          const grouped = base.features_by_level.reduce((acc: Record<number, any[]>, feature: any) => {
            const level = Math.max(1, Number(feature?.level || 1));
            if (!acc[level]) acc[level] = [];
            acc[level].push({
              name: String(feature?.name || ''),
              description: String(feature?.description || feature?.description_md || ''),
              ...(feature?.action_type ? { action_type: feature.action_type } : {}),
              ...(feature?.uses?.count ? { uses: feature.uses } : {}),
            });
            return acc;
          }, {});
          base.features_by_level = Object.entries(grouped)
            .map(([level, features]) => ({ level: Number(level), features }))
            .sort((a, b) => a.level - b.level);
        } else {
          base.features_by_level = base.features_by_level.map((entry: any) => ({
            level: Math.max(1, Number(entry?.level || 1)),
            features: Array.isArray(entry?.features) ? entry.features.map((feature: any) => ({
              name: String(feature?.name || ''),
              description: String(feature?.description || feature?.description_md || ''),
              ...(feature?.action_type ? { action_type: feature.action_type } : {}),
              ...(feature?.uses?.count ? { uses: feature.uses } : {}),
            })) : [],
          }));
        }
      }

      // Normalize skill choices
      if (base.skill_choices && typeof base.skill_choices === 'object') {
        const count = Number(base.skill_choices.count ?? base.skill_choices.choose ?? 0);
        const available = Array.isArray(base.skill_choices.available)
          ? base.skill_choices.available.map((v: any) => String(v))
          : Array.isArray(base.skill_choices.from)
            ? base.skill_choices.from.map((v: any) => String(v))
          : [];
        if (count > 0 && available.length > 0) {
          base.skill_choices = { count, available, choose: count, from: available };
        } else {
          delete base.skill_choices;
        }
      }

      // Normalize spellcasting config
      if (base.spellcasting && typeof base.spellcasting === 'object') {
        const typeValue = String(base.spellcasting.type || 'none').toLowerCase();
        if (typeValue === 'none') {
          base.spellcasting = { type: 'none' };
        } else {
          base.spellcasting = {
            ...base.spellcasting,
            type: base.spellcasting.type || 'known',
            level_acquired: Math.max(1, Number(base.spellcasting.level_acquired ?? base.spellcasting.gained_at_level ?? 1)),
            spell_list: Array.isArray(base.spellcasting.spell_list)
              ? base.spellcasting.spell_list.map((v: any) => String(v))
              : [],
          };
        }
      }

      // Normalize starting equipment entries
      if (Array.isArray(base.starting_equipment)) {
        base.starting_equipment = base.starting_equipment
          .map((entry: any) => {
            if (typeof entry === 'string') {
              return { name: entry, qty: 1 };
            }
            if (entry && typeof entry === 'object') {
              return {
                name: String(entry.name || entry.slug || ''),
                qty: Math.max(1, Number(entry.qty ?? entry.quantity ?? 1)),
              };
            }
            return null;
          })
          .filter((entry: any) => entry && entry.name);
      }
    }
  }
  if (type === 'background') {
    if (base.languages && typeof base.languages === 'object' && !Array.isArray(base.languages)) {
      // keep choose/fixed model as-is
    } else {
      base.languages = asStringArray(base.languages);
    }
  }
  if (type === 'spell') {
    const level = Number(base.level);
    base.level = Number.isFinite(level) ? Math.min(9, Math.max(0, Math.floor(level))) : 0;
    base.school = String(base.school || '').trim();
    base.casting_time = String(base.casting_time || '').trim();
    base.range = String(base.range || '').trim();
    base.duration = String(base.duration || '').trim();
    base.description = String(base.description || '').trim();
    base.classes = asStringArray(base.classes);
    base.components = normalizeSpellComponents(base.components, base.material);

    if (base.damage && typeof base.damage === 'object') {
      const dice = String(base.damage.dice || '').trim();
      const typeValue = String(base.damage.type || '').trim();
      if (!dice) {
        delete base.damage;
      } else {
        base.damage = { dice, ...(typeValue ? { type: typeValue.toLowerCase() } : {}) };
      }
    }
    if (base.healing && typeof base.healing === 'object') {
      const dice = String(base.healing.dice || '').trim();
      const typeValue = String(base.healing.type || '').trim();
      if (!dice) {
        delete base.healing;
      } else {
        base.healing = { dice, ...(typeValue ? { type: typeValue.toLowerCase() } : {}) };
      }
    }
    if (typeof base.higher_levels === 'string') base.higher_levels = base.higher_levels.trim();
    if (typeof base.save === 'string') base.save = base.save.trim();
    if (typeof base.attack_type === 'string') base.attack_type = base.attack_type.trim();
  }
  if (type === 'language') {
    if (!base.name && typeof data?.name === 'string') base.name = data.name;
    if (!base.entries) base.entries = [];
  }

  if (type === 'weapon') {
    const cat = String(base.category || 'simple').toLowerCase();
    base.category = cat === 'martial' || cat === 'simple' ? cat : 'simple';
    base.weapon_type = base.weapon_type === 'ranged' ? 'ranged' : 'melee';
    if (base.damage && typeof base.damage === 'object') {
      const dice = String(base.damage.dice || '1d6').trim();
      const dt = String(base.damage.type || 'slashing').trim().toLowerCase();
      base.damage = { dice, type: dt };
    } else {
      base.damage = { dice: '1d6', type: 'slashing' };
    }
    if (base.versatile_damage && typeof base.versatile_damage === 'object') {
      const vd = base.versatile_damage;
      const d = String(vd.dice || '').trim();
      if (!d) delete base.versatile_damage;
      else {
        base.versatile_damage = {
          dice: d,
          type: String(vd.type || base.damage.type).trim().toLowerCase(),
        };
      }
    }
    if (Array.isArray(base.extra_damage)) {
      base.extra_damage = base.extra_damage
        .map((d: any) => ({
          dice: String(d?.dice || '').trim(),
          type: String(d?.type || '').trim().toLowerCase(),
        }))
        .filter((d: any) => d.dice && d.type);
      if (base.extra_damage.length === 0) delete base.extra_damage;
    }
    if (base.range && typeof base.range === 'object') {
      if (base.weapon_type === 'ranged') {
        base.range = {
          normal: Math.max(0, Number(base.range.normal) || 30),
          long: Math.max(0, Number(base.range.long) || 120),
        };
      } else {
        delete base.range;
      }
    }
    if (Array.isArray(base.properties)) {
      base.properties = base.properties.map((p: any) => String(p).trim()).filter(Boolean);
      if (base.properties.length === 0) delete base.properties;
    }
    if (typeof base.bonus_effects === 'string') {
      const b = base.bonus_effects.trim();
      base.bonus_effects = b || undefined;
    }
  }

  if (type === 'armor') {
    const cat = String(base.category || base.armor_type || 'light').toLowerCase();
    const allowed = ['light', 'medium', 'heavy', 'shield'];
    base.category = allowed.includes(cat) ? cat : 'light';
    base.armor_type = allowed.includes(String(base.armor_type || '').toLowerCase())
      ? String(base.armor_type).toLowerCase()
      : base.category;
    if (base.ac && typeof base.ac === 'object') {
      const ac = base.ac;
      const b = Number(ac.base);
      base.ac = {
        base: Number.isFinite(b) ? b : 11,
        ...(ac.dex_cap !== undefined ? { dex_cap: ac.dex_cap === null ? null : Number(ac.dex_cap) } : {}),
        ...(ac.stealth_disadvantage != null ? { stealth_disadvantage: !!ac.stealth_disadvantage } : {}),
        ...(ac.str_req !== undefined ? { str_req: ac.str_req === null ? null : Number(ac.str_req) } : {}),
      };
      if (Number.isNaN(base.ac.base)) base.ac.base = 11;
    } else {
      base.ac = { base: 11 };
    }
    if (base.resistances) base.resistances = asStringArray(base.resistances);
    if (base.immunities) base.immunities = asStringArray(base.immunities);
    if (Array.isArray(base.resistances) && base.resistances.length === 0) delete base.resistances;
    if (Array.isArray(base.immunities) && base.immunities.length === 0) delete base.immunities;
  }

  if (type === 'item') {
    const legacyType = String((data as any)?.type || (base as any).type || '').trim();
    const legacyMap: Record<string, string> = {
      Weapon: 'weapon',
      Armor: 'armor',
      Gear: 'gear',
      Tool: 'tool',
      Consumable: 'consumable',
      'Magic Item': 'magic',
    };
    if (legacyType && legacyMap[legacyType]) {
      base.category = legacyMap[legacyType];
    }
    base.category = String(base.category || 'gear').toLowerCase();
    delete (base as any).type;
    if (base.requires_attunement === 'true' || base.requires_attunement === 'false') {
      base.requires_attunement = base.requires_attunement === 'true';
    }
    if (typeof base.requires_attunement === 'string') {
      base.requires_attunement = base.requires_attunement.toLowerCase() === 'true';
    }
    base.description = String(base.description || '').trim();
    if (Array.isArray(base.effects)) {
      base.effects = base.effects
        .filter((e: any) => e && typeof e === 'object')
        .map((e: any) => ({
          name: e.name != null ? String(e.name) : undefined,
          description: e.description != null ? String(e.description) : undefined,
        }));
      if (base.effects.length === 0) delete base.effects;
    }
    if (base.charges != null && base.charges !== '') {
      const n = Number(base.charges);
      base.charges = Number.isFinite(n) ? n : undefined;
    }
  }

  if (type === 'monster') {
    if (typeof base.speed === 'string') {
      const m = String(base.speed).match(/(\d+)\s*ft/i);
      const walk = m ? Number(m[1]) : 30;
      base.speed = { walk: Number.isFinite(walk) ? walk : 30 };
    } else if (base.speed && typeof base.speed === 'object' && !Array.isArray(base.speed)) {
      const s = { ...base.speed };
      if (s.walk != null) s.walk = Number(s.walk) || 0;
      if (s.fly != null) s.fly = Number(s.fly) || 0;
      if (s.swim != null) s.swim = Number(s.swim) || 0;
      if (s.climb != null) s.climb = Number(s.climb) || 0;
      if (s.burrow != null) s.burrow = Number(s.burrow) || 0;
      base.speed = s;
    }
    if (base.hit_points && typeof base.hit_points === 'object') {
      const hp = base.hit_points;
      const avg = Number(hp.average);
      if (Number.isFinite(avg)) base.hit_points = { ...hp, average: Math.floor(avg) };
      if (!base.hit_points.formula && base.hit_points.average != null) {
        const a = Number(base.hit_points.average);
        base.hit_points.formula = Number.isFinite(a) ? `${Math.max(1, Math.floor(a / 2))}d8` : '3d8';
      }
    }
    if (base.ability_scores && typeof base.ability_scores === 'object') {
      const mapKey = (k: string) => {
        const lower = k.toLowerCase();
        const map: Record<string, string> = {
          str: 'strength', dex: 'dexterity', con: 'constitution', int: 'intelligence', wis: 'wisdom', cha: 'charisma',
        };
        return map[lower] || k;
      };
      const out: Record<string, number> = {};
      Object.entries(base.ability_scores).forEach(([k, v]) => {
        const nk = mapKey(k);
        const n = Number(v);
        if (Number.isFinite(n)) out[nk] = Math.min(30, Math.max(0, Math.floor(n)));
      });
      base.ability_scores = out;
    }
    const normalizeActionish = (arr: any) => {
      if (!Array.isArray(arr)) return arr;
      return arr
        .map((a: any) => {
          if (!a || typeof a !== 'object') return a;
          const name = String(a.name || '').trim();
          if (!name) return a;
          const o: any = { ...a, name, description: a.description != null ? String(a.description) : '' };
          if (a.damage != null) o.damage = String(a.damage);
          if (a.attack_bonus != null && a.attack_bonus !== '') o.attack_bonus = Number(a.attack_bonus);
          if (a.save_dc != null && a.save_dc !== '') o.save_dc = Number(a.save_dc);
          if (a.damage_roll && typeof a.damage_roll === 'object') {
            o.damage_roll = {
              dice: String(a.damage_roll.dice || '').trim(),
              type: String(a.damage_roll.type || '').trim().toLowerCase(),
            };
          }
          return o;
        })
        .filter((a: any) => a && (typeof a === 'object' ? String(a.name || '').trim() : true));
    };
    base.actions = normalizeActionish(base.actions);
    if (Array.isArray(base.traits)) base.traits = normalizeActionish(base.traits);
    if (Array.isArray(base.reactions)) base.reactions = normalizeActionish(base.reactions);
    if (Array.isArray(base.legendary_actions)) base.legendary_actions = normalizeActionish(base.legendary_actions);
  }

  base.schema_version = base.schema_version || HOMEBREW_SCHEMA_VERSION;
  return base;
}

export function normalizeLegacyHomebrewEntry(entry: any) {
  if (!entry) return entry;
  const type = String(entry.type || '').toLowerCase();
  const normalizedData = normalizeStructuredHomebrewData(type, entry.data || {});
  return {
    ...entry,
    type,
    data: normalizedData,
    schema_version: entry.schema_version || normalizedData.schema_version || HOMEBREW_SCHEMA_VERSION,
  };
}
